import { motion } from "framer-motion";
import { Users, Target, LayoutGrid, Layers3 } from "lucide-react";
import SectionLabel from "./SectionLabel";
import WhatsAppMark from "./WhatsAppMark";

const STRUCTURE = [
  {
    icon: Users,
    title: "City Members Group",
    desc: "Connect with professionals across industries within your City Chapter and build your wider local network.",
  },
  {
    icon: Target,
    title: "Relevant Professional Circles",
    desc: "Choose one Primary Circle based on your main field of work and, where relevant, one Secondary Circle for another field you are actively involved in.",
  },
  {
    icon: LayoutGrid,
    title: "Additional Dedicated Spaces",
    desc: "Dedicated spaces for business-related promotions and social media content, as well as casual interactions and plans.",
  },
];

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay },
});

const BODY = "text-base md:text-lg text-black/70 leading-relaxed";

export default function HowItWorks() {
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="section-pad bg-white text-black relative overflow-hidden"
    >
      <div className="container-x relative">
        <motion.div {...reveal()}>
          <SectionLabel>How It Works</SectionLabel>
          <h2
            data-testid="how-headline"
            className="font-display mt-7 text-xl md:text-2xl font-semibold tracking-tight leading-snug"
          >
            One community, multiple ways to connect.
          </h2>
          <div className="mt-5 space-y-3" data-testid="how-intro">
            <p className={BODY}>
              High Five Access brings people together through City Chapters,
              Professional Circles, and online and offline meetups, giving
              members multiple ways to connect and build relationships.
            </p>
            <p className={BODY}>
              Members stay connected through their City Chapter&rsquo;s WhatsApp
              Community, where they can interact, share ideas, discover
              opportunities, and connect with other members.
            </p>
            <p className={BODY}>
              Offline and online meetups help take these connections beyond the
              groups, creating opportunities to build familiarity, trust, and
              stronger professional relationships.
            </p>
            <p className={BODY}>
              Meetups are optional and members can choose to attend based on
              their availability.
            </p>
          </div>

          <div className="mt-8 flex items-center gap-4" data-testid="how-whatsapp-header">
            <WhatsAppMark size={56} />
            <div className="text-sm leading-relaxed">
              <div className="font-display font-semibold text-black text-base">WhatsApp Community</div>
              <div className="text-black/55">Where the ecosystem lives.</div>
            </div>
          </div>
        </motion.div>

        <motion.div
          {...reveal(0.05)}
          data-testid="how-structure-card"
          className="hfa-orange-edge relative mt-8 md:mt-10 rounded-3xl border bg-white px-5 py-6 sm:px-7 sm:py-7 md:px-9 md:py-8"
        >
          <div className="inline-flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-orange-600 text-white">
              <Layers3 className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2.25} />
            </span>
            <h3
              data-testid="how-structure-heading"
              className="font-display text-xl md:text-2xl font-semibold tracking-tight leading-snug"
            >
              WhatsApp Community Structure
            </h3>
          </div>
          <p className="mt-4 text-base md:text-lg text-black/70 leading-relaxed" data-testid="how-structure-subline">
            Each City Chapter has its own WhatsApp Community, including the
            following group structure:
          </p>

          <div className="mt-6 md:mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {STRUCTURE.map(({ icon: Icon, title, desc }, i) => (
              <div
                key={title}
                data-testid={`how-card-${i}`}
                className="group hfa-orange-surface hfa-orange-edge border rounded-2xl p-5 md:p-6"
              >
                <div className="w-10 h-10 rounded-xl bg-black text-white flex items-center justify-center group-hover:bg-orange-600 transition-colors">
                  <Icon className="w-[18px] h-[18px]" />
                </div>
                <h4 className="font-display mt-4 text-xl md:text-[22px] font-semibold tracking-tight leading-snug text-black">
                  {title}
                </h4>
                <p className="mt-1.5 text-[15px] text-black/60 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div {...reveal(0.05)} className="mt-10 md:mt-12 space-y-4">
          <p className={BODY} data-testid="how-after-p1">
            With focused Professional Circles and a shared City Members Group,
            conversations stay relevant while making cross-industry networking
            simple, organized, and meaningful.
          </p>
          <p className={BODY} data-testid="how-after-p2">
            Each City Chapter is supported by dedicated Captains, bringing
            structure, continuity, and local leadership to every community.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
